/**
 * Shared onboarding spreadsheet date parsing (NZ-style + ISO).
 *
 * Aligns with Pacific.Agentic.Api `onboarding_dates.py` so portal validation
 * and Api pre-flight accept the same cell formats. Christmas still receives
 * ISO `yyyy-MM-dd` after Api normalisation.
 *
 * Ambiguity policy: ISO `YYYY-MM-DD` first; otherwise day/month/year with
 * `/` or `-` separators (never US month-first). Two-digit years use `20YY`.
 */

export const ACCEPTED_ONBOARDING_DATE_FORMATS =
  "DD/MM/YYYY, DD-MM-YYYY, DD/MM/YY, DD-MM-YY, YYYY-MM-DD, or YYYY/MM/DD";

export type OnboardingDateResult =
  | { value: string | null }
  | { error: string };

/**
 * Normalise an optional onboarding date cell to `yyyy-MM-dd`.
 * Blank → `{ value: null }`. Invalid → `{ error }` with `fieldName` embedded.
 */
export function parseOnboardingDate(
  raw: string,
  fieldName: string,
): OnboardingDateResult {
  const trimmed = raw.trim();
  if (!trimmed) return { value: null };

  let iso: string | undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    iso = trimmed;
  } else {
    const isoSlash = trimmed.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
    // Separator must match on both sides (reject mixed `31/12-2025`).
    const fullYear = trimmed.match(/^(\d{1,2})([/-])(\d{1,2})\2(\d{4})$/);
    const shortYear = trimmed.match(/^(\d{1,2})([/-])(\d{1,2})\2(\d{2})$/);
    if (isoSlash) {
      const [, year, month, day] = isoSlash;
      iso = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    } else if (fullYear) {
      const [, day, , month, year] = fullYear;
      iso = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    } else if (shortYear) {
      const [, day, , month, year] = shortYear;
      iso = `20${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    } else {
      return {
        error: `invalid ${fieldName} '${trimmed}'. Expected ${ACCEPTED_ONBOARDING_DATE_FORMATS}.`,
      };
    }
  }

  const [year, month, day] = iso.split("-").map(Number);
  // Date.UTC maps years 0–99 → 1900–1999; setUTCFullYear restores the parsed year
  // so Api Python `date(year, …)` parity holds for padded years like 0001-01-01.
  const probe = new Date(Date.UTC(year, month - 1, day));
  probe.setUTCFullYear(year, month - 1, day);
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() + 1 !== month ||
    probe.getUTCDate() !== day
  ) {
    return {
      error: `invalid ${fieldName} '${trimmed}'. Expected ${ACCEPTED_ONBOARDING_DATE_FORMATS}.`,
    };
  }
  return { value: iso };
}

/** True when `raw` is blank or a supported onboarding date format. */
export function isValidOnboardingDateInput(raw: string): boolean {
  return !("error" in parseOnboardingDate(raw, "date"));
}

/** Convert ISO `yyyy-MM-dd` to NZ `DD/MM/YYYY` for editors / CSV re-export. */
export function onboardingIsoToDisplayDate(value: string | null): string {
  if (!value) return "";
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
}
